import { ValidatorFn, Validators } from '@angular/forms';
import { TypedFormControl, TypedFormGroup } from '@principle-theorem/ng-shared';
import {
  DateFilterType,
  DYNAMIC_DATE_COMPARISONS,
  ReportFilterComparison,
  TimingDirection,
  TimingUnit,
} from '@principle-theorem/principle-core/interfaces';
import { ISelectOption } from '@principle-theorem/shared';
import { toPairs } from 'lodash';
import * as moment from 'moment-timezone';
import { BehaviorSubject, Observable } from 'rxjs';

export const DATE_FILTER_TYPE_OPTIONS: ISelectOption<DateFilterType>[] = [
  {
    label: 'Select date when running the report',
    value: DateFilterType.ReportDate,
  },
  {
    label: 'Use specific date',
    value: DateFilterType.SpecificDate,
  },
];

export const DATE_RANGE_FILTER_TYPE_OPTIONS: ISelectOption<DateFilterType>[] = [
  {
    label: 'Select date range when running the report',
    value: DateFilterType.ReportDate,
  },
  {
    label: 'Use a specific Date Range',
    value: DateFilterType.SpecificDate,
  },
];

export function isDynamicDateFilter(
  comparison?: ReportFilterComparison
): boolean {
  return !!comparison && DYNAMIC_DATE_COMPARISONS.includes(comparison);
}

export function isDateRangeFilter(
  comparison?: ReportFilterComparison
): boolean {
  return !comparison || comparison === ReportFilterComparison.DateIsBetween;
}

export interface IDateFilterForm {
  comparison: ReportFilterComparison;
  dateType: DateFilterType;
  date: moment.Moment;
  dateFrom: moment.Moment;
  dateTo: moment.Moment;
  direction: TimingDirection;
  amount: number;
  unit: TimingUnit;
}

type ValidatorMap = {
  [field in keyof IDateFilterForm]: ValidatorFn[];
};

interface IFormState {
  dateTypes: ISelectOption<DateFilterType>[];
  showDateTypeField: boolean;
  showSpecificDateSection: boolean;
  showSpecificDateRangeSection: boolean;
  showReportDateSection: boolean;
  showDynamicDateSection: boolean;
  validators: ValidatorMap;
}

export class EditReportFilterDateForm extends TypedFormGroup<IDateFilterForm> {
  private _state$ = new BehaviorSubject<IFormState>({
    dateTypes: [],
    showDateTypeField: false,
    showSpecificDateSection: false,
    showSpecificDateRangeSection: false,
    showReportDateSection: false,
    showDynamicDateSection: false,
    validators: {
      comparison: [Validators.required],
      dateType: [Validators.required],
      date: [Validators.required],
      dateFrom: [],
      dateTo: [],
      direction: [],
      amount: [],
      unit: [],
    },
  });

  get state$(): Observable<IFormState> {
    return this._state$.asObservable();
  }

  constructor() {
    super({
      comparison: new TypedFormControl<ReportFilterComparison>(
        ReportFilterComparison.DateIs,
        Validators.required
      ),
      dateType: new TypedFormControl<DateFilterType>(DateFilterType.ReportDate),
      direction: new TypedFormControl<TimingDirection>(TimingDirection.Before),
      amount: new TypedFormControl<number>(1, Validators.min(1)),
      unit: new TypedFormControl<TimingUnit>(TimingUnit.Days),
      date: new TypedFormControl<moment.Moment>(moment()),
      dateFrom: new TypedFormControl<moment.Moment>(moment()),
      dateTo: new TypedFormControl<moment.Moment>(moment()),
    });
  }

  syncState(
    comparison: ReportFilterComparison = ReportFilterComparison.DateIsBetween,
    dateType: DateFilterType = DateFilterType.ReportDate
  ): void {
    const formState = this._getFormState(comparison, dateType);
    this._state$.next(formState);
    this._setValidators(formState.validators);
  }

  private _getFormState(
    comparison: ReportFilterComparison,
    dateType: DateFilterType
  ): IFormState {
    if (isDynamicDateFilter(comparison)) {
      return DYNAMIC_FORM_STATE;
    }
    if (isDateRangeFilter(comparison)) {
      return dateType === DateFilterType.ReportDate
        ? REPORT_DATE_RANGE_STATE
        : SPECIFIC_DATE_RANGE_STATE;
    }
    return dateType === DateFilterType.ReportDate
      ? REPORT_DATE_STATE
      : SPECIFIC_DATE_STATE;
  }

  private _setValidators(validatorMap: ValidatorMap): void {
    toPairs(validatorMap).map(([field, validators]) => {
      const formCtrl = this.get(field);
      if (!formCtrl) {
        return;
      }
      formCtrl.setValidators(validators);
    });
    this.updateValueAndValidity({ emitEvent: false });
  }
}

const DYNAMIC_FORM_STATE = {
  dateTypes: [],
  showDateTypeField: false,
  showSpecificDateSection: false,
  showSpecificDateRangeSection: false,
  showReportDateSection: false,
  showDynamicDateSection: true,
  validators: {
    comparison: [Validators.required],
    dateType: [],
    date: [],
    dateFrom: [],
    dateTo: [],
    direction: [Validators.required],
    amount: [Validators.required],
    unit: [Validators.required],
  },
};

const SPECIFIC_DATE_RANGE_STATE = {
  dateTypes: DATE_RANGE_FILTER_TYPE_OPTIONS,
  showDateTypeField: true,
  showSpecificDateSection: false,
  showSpecificDateRangeSection: true,
  showReportDateSection: false,
  showDynamicDateSection: false,
  validators: {
    comparison: [Validators.required],
    dateType: [Validators.required],
    date: [],
    dateFrom: [Validators.required],
    dateTo: [Validators.required],
    direction: [],
    amount: [],
    unit: [],
  },
};

const SPECIFIC_DATE_STATE = {
  dateTypes: DATE_FILTER_TYPE_OPTIONS,
  showDateTypeField: true,
  showSpecificDateSection: true,
  showSpecificDateRangeSection: false,
  showReportDateSection: false,
  showDynamicDateSection: false,
  validators: {
    comparison: [Validators.required],
    dateType: [Validators.required],
    date: [Validators.required],
    dateFrom: [],
    dateTo: [],
    direction: [],
    amount: [],
    unit: [],
  },
};

const REPORT_DATE_RANGE_STATE = {
  dateTypes: DATE_RANGE_FILTER_TYPE_OPTIONS,
  showDateTypeField: true,
  showSpecificDateSection: false,
  showSpecificDateRangeSection: false,
  showReportDateSection: true,
  showDynamicDateSection: false,
  validators: {
    comparison: [Validators.required],
    dateType: [Validators.required],
    date: [],
    dateFrom: [],
    dateTo: [],
    direction: [],
    amount: [],
    unit: [],
  },
};

const REPORT_DATE_STATE = {
  dateTypes: DATE_FILTER_TYPE_OPTIONS,
  showDateTypeField: true,
  showSpecificDateSection: false,
  showSpecificDateRangeSection: false,
  showReportDateSection: true,
  showDynamicDateSection: false,
  validators: {
    comparison: [Validators.required],
    dateType: [Validators.required],
    date: [],
    dateFrom: [],
    dateTo: [],
    direction: [],
    amount: [],
    unit: [],
  },
};
